import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { message } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Entity,
  StaffEntityDetailsContainerEntityDeleteDocument,
  StaffEntityDetailsContainerEntityDocument,
  StaffEntityDetailsContainerStaffUserCurrentDocument,
  StaffEntitySearchContainerEntitySearchDocument,
  StaffUser
} from '../../../../generated.tsx';
import { useOid } from '../../../../hooks/useOid.ts';
import { Country } from '../../../../types/country.ts';
import { ComponentQueryLoader } from '../../../shared/component-query-loader.tsx';
import { getStaffUserCurrentQueryOptionsWithOidAndCacheDisabled } from '../../../shared/utils/queryOptions';
import { EntityDetails } from './entity-details.tsx';
import { lookupCountryQuery } from './staff-components-country-query.tsx';

interface EntityDetailsContainerProps {}

export const EntityDetailsContainer: React.FC<EntityDetailsContainerProps> = (_props) => {
  const { entityId } = useParams<{ entityId: string }>();
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const oid = useOid();
  const staffUserCurrentQueryOptions = getStaffUserCurrentQueryOptionsWithOidAndCacheDisabled(oid);

  const { data: countryData, loading: countryListLoading, error: countryListError } = useQuery(lookupCountryQuery);
  const { data: currentStaffData } = useQuery(StaffEntityDetailsContainerStaffUserCurrentDocument, staffUserCurrentQueryOptions);

  const [_pollForUpdatedEntityList, { data: pollForUpdatedEntityListData, startPolling: startPollingForUpdatedEntityList, stopPolling: stopPollingForUpdatedEntityList }] =
    useLazyQuery(StaffEntitySearchContainerEntitySearchDocument, { fetchPolicy: 'network-only' });
  const [deleteEntity] = useMutation(StaffEntityDetailsContainerEntityDeleteDocument);

  const {
    data: entityList,
    loading: entityListLoading,
    error: entityListError
  } = useQuery(StaffEntityDetailsContainerEntityDocument, {
    variables: {
      id: entityId || ''
    }
  });

  useEffect(() => {
    if (pollForUpdatedEntityListData?.entitySearch?.entityResults) {
      const isEntityDeleted = pollForUpdatedEntityListData.entitySearch.entityResults.every((entity) => entity?.id !== entityId);
      if (isEntityDeleted) {
        stopPollingForUpdatedEntityList();
        setIsProcessing(false);
        message.success('Entity deleted successfully.');
        navigate(-1);
      }
    }
  }, [pollForUpdatedEntityListData]);

  const onDeleteEntity = async (entityId: string) => {
    try {
      setIsProcessing(true);
      const response = await deleteEntity({
        variables: {
          entityDeleteId: entityId
        }
      });

      if (response?.data?.entityDelete?.status?.success) {
        startPollingForUpdatedEntityList(500);
      } else {
        message.error('Failed to delete the entity: ' + response?.data?.entityDelete?.status?.errorMessage);
        setIsProcessing(false);
      }
    } catch (err: any) {
      message.error('Failed to delete the entity');
      setIsProcessing(false);
    }
  };

  return (
    <ComponentQueryLoader
      loading={entityListLoading || countryListLoading}
      hasData={entityList}
      error={entityListError || countryListError}
      hasDataComponent={
        <EntityDetails
          entity={entityList?.entity as Entity}
          onDeleteEntity={onDeleteEntity}
          deleteLoading={isProcessing}
          countryData={countryData?.countries?.eventPayload?.countries as Country[]}
          currentStaffData={currentStaffData?.staffUserCurrent as StaffUser}
        />
      }
    />
  );
};
